import * as React from 'react';
import { HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface Props {
  /** The verbose explanation. Pre-translated. */
  text: string;
  /** Optional className for the trigger button. */
  className?: string;
  /** Optional aria-label override. Defaults to "help". */
  ariaLabel?: string;
}

/**
 * Small `?` icon rendered next to a label. On hover/focus it pops a
 * Radix tooltip with the full explanation. Caller is responsible for
 * pre-translating `text` (i18n keys live under `help.<form>.<field>`
 * in common.json — keep copy under ~2 lines / 140 chars).
 *
 * Why the affordance: many BO forms expose domain-heavy fields
 * (turnover multiplier, bips, apply-on, conditions JSON) that aren't
 * self-explanatory from the label alone. Inline hints already cover
 * the quick cases — this icon is for the longer "what does this do
 * and when should I use it" sentences without crowding the form.
 */
export function FieldHelp({ text, className, ariaLabel }: Props) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel ?? 'help'}
          className={cn(
            'inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            className,
          )}
        >
          <HelpCircle className="h-3.5 w-3.5" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="whitespace-pre-line text-left leading-relaxed">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Inline label + FieldHelp pair — convenience wrapper used by every
 * BO form to keep the call-site to one line. Pass already-translated
 * `text` and `help` strings; this component is dumb on purpose.
 */
export function LabelWithHelp({ text, help }: { text: React.ReactNode; help: string }) {
  return (
    <span className="flex items-center gap-1.5">
      {text}
      <FieldHelp text={help} />
    </span>
  );
}
